import { Box, Heading, useToast, Icon, HStack } from '@chakra-ui/react';
import { RiAddLine } from 'react-icons/ri';
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import debounce from 'lodash.debounce';
import { DefaultLayout } from '../../../_layout/DefaultLayout';
import { LinkButton } from '../../../../../components/LinkButton';
import { maskShortDate } from '../../../../../utils/formatters/handleMask';
import { IActivity, ActivityTable } from './components/ActivityTable';
import { listActivitiesService } from '../../../../../services/Activities/ListActivitiesService';
import { translateError } from '../../../../../utils/errors';
import { useActivity } from '../../../../../hooks/activity';
import { useAuth } from '../../../../../hooks/auth';

interface ILoadActivitiesProps {
  name?: string;
  sort?: 'name' | 'isActive' | 'createdAt';
  order?: 'ASC' | 'DESC';
  page?: number;
}

export const ActivityList = (): JSX.Element => {
  const toast = useToast();

  const { user } = useAuth();

  const { handleActivity } = useActivity();

  const [activitiesList, setActivitiesList] = useState<IActivity[]>([]);
  const [currentFilters, setCurrentFilters] =
    useState<{ [key: string]: string }>();
  const [currentOrdering, setCurrentOrdering] = useState<'ASC' | 'DESC'>('ASC');
  const [currentPage, setCurrentPage] = useState(1);
  const [filterBy, setFilterBy] = useState('name');
  const [sortBy, setSortBy] = useState<'name' | 'isActive' | 'createdAt'>(
    'createdAt',
  );
  const [totalPages, setTotalPages] = useState<number>();

  const loadActivities = useCallback(
    async ({ name, order, page, sort }: ILoadActivitiesProps) => {
      try {
        const activities = await listActivitiesService({
          name,
          limit: 20,
          page,
          sort,
          order,
        });

        const parsedActivities = activities.items.map((activity) => ({
          ...activity,
          formattedCreatedAt: maskShortDate(activity.createdAt),
        }));

        setTotalPages(activities.pages);
        setActivitiesList(parsedActivities);
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status !== 401) {
          toast({
            title: 'Failed to load data',
            description:
              translateError({ message: err.response?.data.message }) ||
              'An error occurred while loading activity data, please try again.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        }
      }
    },
    [toast],
  );

  useEffect(() => {
    loadActivities({
      ...currentFilters,
      order: currentOrdering,
      page: currentPage,
      sort: sortBy,
    });
  }, [
    currentFilters,
    currentPage,
    sortBy,
    loadActivities,
    currentOrdering,
    handleActivity,
  ]);

  const handleFilterBy = useCallback((value: string) => {
    setFilterBy(value);
  }, []);

  const handleFilter = useCallback(
    (value: string) => {
      setCurrentFilters(value ? { [filterBy]: value } : undefined);
    },
    [filterBy],
  );

  const debounceHandleFilter = debounce(handleFilter, 1000);

  const handleSortList = useCallback(
    (sort: 'name' | 'isActive' | 'createdAt') => {
      setSortBy(sort);
    },
    [],
  );

  const handleOrderList = useCallback((order: 'ASC' | 'DESC') => {
    setCurrentOrdering(order);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  return (
    <DefaultLayout>
      <Box flex="1" borderRadius={8} bg="white" p="8">
        <HStack justify="space-between">
          <Heading size="lg" fontWeight="normal">
            Calendars
          </Heading>

          {user.featureGroup.features.some((feature) =>
            ['ACTIVITY_FULL_ACCESS', 'ACTIVITY_WRITE_ACCESS'].includes(
              feature.key,
            ),
          ) && (
            <LinkButton
              colorScheme="green"
              icon={<Icon as={RiAddLine} fontSize="16" />}
              to="/activities/register"
            >
              Register new
            </LinkButton>
          )}
        </HStack>

        <ActivityTable
          mt="4"
          activities={activitiesList}
          currentPage={currentPage}
          filterBy={filterBy}
          onChangeFilterBy={handleFilterBy}
          onFilter={debounceHandleFilter}
          onOrder={handleOrderList}
          onPageChange={handlePageChange}
          onSort={handleSortList}
          order={currentOrdering}
          sortBy={sortBy}
          totalPages={totalPages}
        />
      </Box>
    </DefaultLayout>
  );
};
